// Courses & tee boxes.
//
// This is where the stroke calculation gets its accuracy. A 9-hole course
// handicap needs THREE numbers per nine, per tee: course rating, slope and
// par. With them, two players on different tees are compared correctly.
// Without them, the app falls back to an estimate and says so.

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, MapPin, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import ReadOnlyNotice from './ReadOnlyNotice';
import { teeSetHasNineData } from '../utils/handicap';
import { tierOptions, tierCoverageGaps, untieredTeeSets } from '../utils/teeTiers';
import { GHIN_API_ENABLED } from '../utils/constants';

function NineInputs({ tee, prefix, label, onChange, disabled }) {
  const f = (k) => tee[`${prefix}_${k}`] ?? '';
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
        {teeSetHasNineData(tee, prefix === 'f9' ? 'F9' : 'B9')
          ? <CheckCircle2 className="h-4 w-4 text-emerald-600" title="Complete" />
          : <AlertTriangle className="h-4 w-4 text-amber-500" title="Incomplete - strokes will be estimated" />}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Rating</Label>
          <Input type="number" step="0.1" disabled={disabled} value={f('rating')}
            onChange={(e) => onChange(`${prefix}_rating`, e.target.value)} placeholder="35.4" />
        </div>
        <div>
          <Label className="text-xs">Slope</Label>
          <Input type="number" step="1" disabled={disabled} value={f('slope')}
            onChange={(e) => onChange(`${prefix}_slope`, e.target.value)} placeholder="128" />
        </div>
        <div>
          <Label className="text-xs">Par</Label>
          <Input type="number" step="1" disabled={disabled} value={f('par')}
            onChange={(e) => onChange(`${prefix}_par`, e.target.value)} placeholder="36" />
        </div>
      </div>
    </div>
  );
}

export default function CoursesTab({
  courses, teeSets, isAdmin,
  onSaveCourse, onDeleteCourse, onSaveTeeSet, onDeleteTeeSet, onSyncGhin,
}) {
  const [drafts, setDrafts] = useState({});     // teeSetId -> partial edits
  const [newCourse, setNewCourse] = useState({ id: '', name: '' });
  const [newTee, setNewTee] = useState({});     // courseId -> name

  const teesByCourse = useMemo(() => {
    const m = {};
    (teeSets || []).forEach(t => {
      m[t.course_id] = m[t.course_id] || [];
      m[t.course_id].push(t);
    });
    Object.values(m).forEach(list => list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return m;
  }, [teeSets]);

  const merged = (tee) => ({ ...tee, ...(drafts[tee.id] || {}) });
  const isDirty = (tee) => !!drafts[tee.id] && Object.keys(drafts[tee.id]).length > 0;

  const edit = (teeId, field, value) =>
    setDrafts(d => ({ ...d, [teeId]: { ...(d[teeId] || {}), [field]: value } }));

  const save = async (tee) => {
    const m = merged(tee);
    const numeric = ['f9_rating', 'f9_slope', 'f9_par', 'b9_rating', 'b9_slope', 'b9_par',
                     'yardage_f9', 'yardage_b9', 'sort_order', 'tee_tier'];
    const payload = { ...m };
    numeric.forEach(k => {
      payload[k] = payload[k] === '' || payload[k] === null || payload[k] === undefined
        ? null : Number(payload[k]);
    });
    delete payload.created_at;
    delete payload.updated_at;
    await onSaveTeeSet(payload);
    setDrafts(d => { const c = { ...d }; delete c[tee.id]; return c; });
  };

  const tiers = useMemo(() => tierOptions(teeSets, courses), [teeSets, courses]);
  const tierGaps = useMemo(() => tierCoverageGaps(teeSets, courses), [teeSets, courses]);
  const noTier = useMemo(() => untieredTeeSets(teeSets), [teeSets]);

  const incompleteCount = (teeSets || []).filter(
    t => !teeSetHasNineData(t, 'F9') || !teeSetHasNineData(t, 'B9')
  ).length;

  return (
    <div className="mt-4 space-y-6">
      {!isAdmin && <ReadOnlyNotice>Course and tee data is view-only. Unlock admin to edit.</ReadOnlyNotice>}

      {incompleteCount > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">{incompleteCount} tee set{incompleteCount === 1 ? '' : 's'} missing rating / slope / par.</p>
            <p className="mt-1 text-amber-800">
              Until these are filled in, strokes given are <em>estimated</em> from handicap index
              (half the difference for 9 holes) and the tee-box adjustment is not applied. Matches
              where two players use different tees will be the least accurate.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
        <p className="font-medium">Tee tiers</p>
        <p className="mt-1">
          A <strong>tier</strong> groups equivalent tees across courses so players choose a tee
          level once instead of one tee per course. Give the same tier number to the tees that
          belong together:
        </p>
        <ul className="mt-1 space-y-0.5">
          {tiers.map(t => (
            <li key={t.value}><strong>Tier {t.value}</strong> ({t.label}) &mdash; {t.detail}</li>
          ))}
        </ul>
      </div>

      {tierGaps.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Incomplete tier coverage.</p>
            <ul className="mt-1 list-inside list-disc">
              {tierGaps.map((gap, i) => (
                <li key={i}>Tier {gap.tier} has no tee at {gap.course.name}</li>
              ))}
            </ul>
            <p className="mt-1">Players on that tier fall back to the first tee at that course.</p>
          </div>
        </div>
      )}

      {noTier.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {noTier.length} tee set{noTier.length === 1 ? '' : 's'} ({noTier.map(t => t.name).join(', ')})
            {noTier.length === 1 ? ' has' : ' have'} no tier, so no player can choose
            {noTier.length === 1 ? ' it' : ' them'} as a default.
          </span>
        </div>
      )}

      <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
        <p className="font-medium">How strokes are calculated</p>
        <p className="mt-1">
          9-hole Course Handicap = (Index &divide; 2) &times; (Slope &divide; 113) + (Rating &minus; Par).
          Strokes given = the difference between the two players' course handicaps, played off the low man.
          The <strong>Rating &minus; Par</strong> term is the tee-box adjustment: it is what makes a White-tee
          player and a Gray-tee player directly comparable.
        </p>
      </div>

      {GHIN_API_ENABLED && isAdmin && (
        <Button variant="outline" onClick={onSyncGhin}>
          <RefreshCw className="mr-2 h-4 w-4" /> Sync tees &amp; ratings from GHIN
        </Button>
      )}
      {!GHIN_API_ENABLED && (
        <p className="text-xs text-gray-500">
          GHIN sync is switched off for 2026 (<code>GHIN_API_ENABLED</code> in
          <code> src/utils/constants.js</code>). Turn it on to pull tee sets and ratings automatically
          instead of entering them here.
        </p>
      )}

      {(courses || []).map(course => (
        <Card key={course.id}>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" /> {course.name}
                </CardTitle>
                <CardDescription>GHIN course ID {course.id}</CardDescription>
              </div>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={() => onDeleteCourse(course.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {(teesByCourse[course.id] || []).map(tee => {
              const m = merged(tee);
              return (
                <div key={tee.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        className="h-8 w-32 font-medium"
                        disabled={!isAdmin}
                        value={m.name || ''}
                        onChange={(e) => edit(tee.id, 'name', e.target.value)}
                      />
                      <div className="flex items-center gap-1">
                        <Label className="text-xs">Tier</Label>
                        <select
                          className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm disabled:opacity-60"
                          disabled={!isAdmin}
                          value={m.tee_tier ?? ''}
                          onChange={(e) => edit(tee.id, 'tee_tier', e.target.value)}
                          title="Groups this tee with the equivalent tee at other courses"
                        >
                          <option value="">(none)</option>
                          {[1, 2, 3, 4, 5].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <span className="text-xs text-gray-400">{tee.id}</span>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-2">
                        {isDirty(tee) && <span className="text-xs text-amber-600">unsaved</span>}
                        <Button size="sm" disabled={!isDirty(tee)} onClick={() => save(tee)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => onDeleteTeeSet(tee.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <NineInputs tee={m} prefix="f9" label="Front 9" disabled={!isAdmin}
                      onChange={(k, v) => edit(tee.id, k, v)} />
                    <NineInputs tee={m} prefix="b9" label="Back 9" disabled={!isAdmin}
                      onChange={(k, v) => edit(tee.id, k, v)} />
                  </div>
                </div>
              );
            })}

            {isAdmin && (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Add a tee box</Label>
                  <Input
                    placeholder="e.g. Blue"
                    value={newTee[course.id] || ''}
                    onChange={(e) => setNewTee(s => ({ ...s, [course.id]: e.target.value }))}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={async () => {
                    const name = (newTee[course.id] || '').trim();
                    if (!name) return;
                    await onSaveTeeSet({
                      id: `${course.id}_${name.toLowerCase().replace(/\s+/g, '_')}`,
                      course_id: course.id,
                      name,
                      gender: 'M',
                      sort_order: (teesByCourse[course.id] || []).length + 1,
                      tee_tier: null,
                      source: 'manual',
                    });
                    setNewTee(s => ({ ...s, [course.id]: '' }));
                  }}
                >
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Add a course</CardTitle>
            <CardDescription>Use the GHIN course ID so ratings can be synced later.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs">Course ID</Label>
                <Input className="w-28" value={newCourse.id}
                  onChange={(e) => setNewCourse(c => ({ ...c, id: e.target.value }))} placeholder="7187" />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Name</Label>
                <Input value={newCourse.name}
                  onChange={(e) => setNewCourse(c => ({ ...c, name: e.target.value }))} placeholder="New Course" />
              </div>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!newCourse.id.trim() || !newCourse.name.trim()) return;
                  await onSaveCourse({
                    id: newCourse.id.trim(),
                    name: newCourse.name.trim(),
                    short_name: newCourse.name.trim(),
                    sort_order: (courses?.length || 0) + 1,
                  });
                  setNewCourse({ id: '', name: '' });
                }}
              >
                <Plus className="mr-1 h-4 w-4" /> Add course
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
